import React from "react";
import { motion } from "framer-motion";
import {
  CheckCircle,
  ArrowRight,
  Zap,
  TrendingUp,
  Building2,
  Rocket,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { HomepageNavbar } from "../../components/HomepageNavbar";
import { HomepageFooter } from "../../components/HomepageFooter";
import { Helmet } from "react-helmet-async";

const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: "Starter",
      icon: Zap,
      description: "Perfect for small events and getting started",
      price: "Free",
      features: [
        "Up to 100 attendees per event",
        "Basic event page builder",
        "Ticket sales and registration",
        "Email support",
        "Basic analytics",
        "QR code check in",
      ],
      cta: "Get Started",
      popular: false,
    },
    {
      name: "Growth",
      icon: TrendingUp,
      description: "For growing teams and multiple events",
      price: "$99",
      period: "/month",
      features: [
        "Up to 1,000 attendees per event",
        "Advanced event page builder",
        "Multiple ticket types",
        "Priority email support",
        "Advanced analytics",
        "Custom branding",
        "Promo codes and discounts",
        "Team collaboration tools",
      ],
      cta: "Start Free Trial",
      popular: true,
    },
    {
      name: "Business",
      icon: Building2,
      description: "For established organizations and enterprises",
      price: "$299",
      period: "/month",
      features: [
        "Unlimited attendees",
        "Full Studio features",
        "Advanced Workspace tools",
        "Dedicated account manager",
        "Custom integrations",
        "API access",
        "White label options",
        "SLA guarantee",
        "Custom training",
      ],
      cta: "Contact Sales",
      popular: false,
    },
    {
      name: "Enterprise",
      icon: Rocket,
      description: "Custom solutions for large scale operations",
      price: "Custom",
      features: [
        "Everything in Business",
        "Multi tenant architecture",
        "Custom development",
        "24/7 phone support",
        "On site training",
        "Custom SLA",
        "Dedicated infrastructure",
        "Compliance assistance",
        "Custom reporting",
      ],
      cta: "Contact Sales",
      popular: false,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Pricing - Event Software Pricing | Event Platform Plans</title>
        <meta
          name="description"
          content="The pricing structure is clear. Each plan supports a specific use case. Clients understand what they pay for."
        />
        <meta
          name="keywords"
          content="event software pricing, event platform plans, event management pricing, enterprise event software"
        />
      </Helmet>
      <div className="min-h-screen bg-white">
        <HomepageNavbar />

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#f3f4ff] via-white to-white border-b border-gray-100">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
                Pricing
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                The pricing structure is clear. Each plan supports a specific
                use case. Clients understand what they pay for.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Plans */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {plans.map((plan, index) => {
                const Icon = plan.icon;
                return (
                  <motion.div
                    key={plan.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`relative bg-white border-2 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all ${
                      plan.popular
                        ? "border-[#17265a] scale-105"
                        : "border-gray-200"
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-[#17265a] text-white px-4 py-1 rounded-full text-xs font-semibold">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="mb-6">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#17265a]/10 text-[#17265a] mb-4">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {plan.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {plan.description}
                      </p>
                    </div>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-gray-900">
                          {plan.price}
                        </span>
                        {plan.period && (
                          <span className="text-gray-600">{plan.period}</span>
                        )}
                      </div>
                    </div>

                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-start gap-3"
                        >
                          <CheckCircle className="w-5 h-5 text-[#17265a] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (
                          plan.name === "Enterprise" ||
                          plan.name === "Business"
                        ) {
                          // Could navigate to contact page
                          navigate("/");
                        } else {
                          navigate("/");
                        }
                      }}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                        plan.popular
                          ? "bg-[#17265a] text-white hover:bg-[#0f1a3f]"
                          : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                      }`}
                    >
                      {plan.cta}
                    </motion.button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <div className="space-y-6">
              {[
                {
                  question: "Can I change plans later?",
                  answer:
                    "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges.",
                },
                {
                  question: "What payment methods do you accept?",
                  answer:
                    "We accept all major credit cards, debit cards, and bank transfers for Business and Enterprise plans.",
                },
                {
                  question: "Is there a setup fee?",
                  answer:
                    "No, there are no setup fees for any plan. You only pay the monthly or annual subscription fee.",
                },
                {
                  question: "Do you offer discounts for annual plans?",
                  answer:
                    "Yes, annual plans receive a 20% discount compared to monthly billing. Contact us for Enterprise pricing.",
                },
                {
                  question: "What happens if I exceed my plan limits?",
                  answer:
                    "We'll notify you before you reach your limits. You can upgrade your plan or purchase additional capacity as needed.",
                },
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Still have questions?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our team is here to help you choose the right plan for your
                needs.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/")}
                  className="bg-[#17265a] text-white px-8 py-4 rounded-lg hover:bg-[#0f1a3f] transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl font-semibold text-lg group"
                >
                  <span>Book a walkthrough</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/")}
                  className="bg-transparent border-2 border-[#17265a] text-[#17265a] px-8 py-4 rounded-lg hover:bg-[#17265a]/5 transition-all duration-300 flex items-center gap-2 font-semibold text-lg"
                >
                  <span>Back to Home</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        <HomepageFooter />
      </div>
    </>
  );
};

export default PricingPage;
